import {
  ComponentPropsWithoutRef,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useCompanySummaryState } from '../../hooks';
import { useTracking } from 'react-tracking';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
} from '@/constants/anchors';
import { COMPANY_SNOWFLAKE } from '@/constants/feedbackSections';
import { SnowflakeType } from '@/components/Snowflake/SnowflakeType';
import {
  SummarySection,
  SnowflakeSection,
  TitleText,
  ReadMoreButton,
  RiskRewardSection,
  RiskRewardWrapper,
  RiskRewardGrid,
  SnowflakeChart,
  SnowflakeTitle,
  SnowflakeCaption,
  SnowflakeWrapper,
  StyledWrapper,
  ScrollContentWrapper,
} from './styled';
import { Button, Grid, Text, useTheme } from '@simplywallst/ui-core';
import loadable from '@loadable/component';
import SnowflakeChartWrapper from '../SnowflakeChartWrapper';
import SnowflakeTable from '../SnowflakeTable';
import RiskReward from '../RiskReward';
import { useVisibleSectionsContext } from '../../../VisibleSectionsContextProvider';
import { isValidSection } from '@/pages/CompanyReport/useSection';
import { SubSectionHeader } from '../../../Overview/components/SubSectionHeader';
import type { CompanySubSection } from '@/pages/CompanyReport/hooks/useReportUrlHandler';
import { SummaryRiskStatus } from '../../interface';
import useSummaryRewards from '../../hooks/useSummaryRewards';
import AboutTheCompanyContent from '../AboutTheCompanyContent';
import ActionSheet, { Panel, Header } from '@/components/ActionSheet';
import RiskRewardSkeleton from '../RiskRewardSkeleton';

const RiskCheckModal = loadable(() => import('../RiskCheckModal'));

const Snowflake = loadable(() => import('@/components/Snowflake/Snowflake'));

const routes: string[] = [
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
];

export function Summary() {
  const {
    isFund,
    description,
    snowflakePoints,
    uniqueSymbol,
    tickerSymbol,
    summaryRiskStatus,
    firstFourFailingRisks,
    allRisks,
    fetchStatementsStatus,
    fundamentalsSentence,
    companyName,
    analysisChecks,
  } = useCompanySummaryState();
  const theme = useTheme();
  const rewardStatements = useSummaryRewards();
  const { trackEvent } = useTracking({ type: 'track', subject: 'summary' });
  const [openRisksModal, setOpenRiskModal] = useState(false);
  const [openAboutCompany, setOpenAboutCompany] = useState(false);
  const { loadSection } = useVisibleSectionsContext();

  useEffect(() => {
    trackEvent({
      action: 'landed',
    });
  }, [trackEvent]);

  const handleSnowflakeClick = useCallback<
    NonNullable<ComponentPropsWithoutRef<typeof Snowflake>['handleClick']>
  >(
    (route) => {
      if (isValidSection(route)) {
        loadSection(route);
      } else {
        console.warn('attempted to redirect to an invalid section:', route);
      }
    },
    [loadSection]
  );

  const getRiskDisplay = () => {
    switch (summaryRiskStatus) {
      case SummaryRiskStatus.HasPassedAllRisks:
        return <p>{`${tickerSymbol} passed our risk checks.`}</p>;
      case SummaryRiskStatus.HasNotFailedAnyRisk:
        return (
          <Text typography="xSmall">{`No risks detected for ${tickerSymbol} from our risk checks.`}</Text>
        );
      case SummaryRiskStatus.InsufficientData:
        return (
          <Text typography="xSmall">
            Insufficient data to perform risk analysis
          </Text>
        );
      default: {
        return null;
      }
    }
  };

  return (
    <SummarySection data-cy-id="company-summary">
      <SubSectionHeader data-cy-id="competitors-title">
        {tickerSymbol ? `${tickerSymbol} Stock Overview` : <>&nbsp;</>}
      </SubSectionHeader>
      <Grid
        gap="16px"
        templateRows="auto auto"
        templateRowsMd="auto"
        templateColumns="1fr"
        templateColumnsMd="1.4fr 1fr"
      >
        <RiskRewardSection>
          <RiskRewardGrid
            templateColumns="1fr"
            templateRows="auto 4em auto auto 1fr"
          >
            <TitleText typography="xSmall" color="mediumGray">
              About the company
            </TitleText>
            <Text typography="xSmall" color="white" lineClamp={2}>
              {description ||
                (fetchStatementsStatus === 'success' &&
                  'No business description')}
            </Text>
            <ReadMoreButton
              display="block"
              onClick={() => {
                setOpenAboutCompany(true);
                trackEvent({
                  modifier: 'about-company-read-more',
                  action: 'click',
                });
              }}
              styleType="loud"
              data-cy-id="company-about-button"
            >
              Read more
            </ReadMoreButton>
            <div>
              <Text typography="xSmall" color="mediumGray">
                Rewards
              </Text>
              <RiskRewardWrapper>
                {fetchStatementsStatus === 'success' ? (
                  <>
                    {rewardStatements
                      .slice(0, 2)
                      .map(
                        ({ index, hash, name, statementId, description }) => (
                          <RiskReward
                            key={`risk-reward-${statementId}`}
                            size="small"
                            name={name}
                            index={index}
                            hash={hash as CompanySubSection}
                            statementId={statementId}
                            description={description}
                          />
                        )
                      )}
                  </>
                ) : (
                  <>
                    <RiskRewardSkeleton />
                    <RiskRewardSkeleton />
                  </>
                )}
              </RiskRewardWrapper>
              <Text typography="xSmall" color="mediumGray">
                Risk analysis
              </Text>
              <RiskRewardWrapper>
                {fetchStatementsStatus === 'success' ? (
                  <>
                    {firstFourFailingRisks.length === 0 && getRiskDisplay()}
                    {firstFourFailingRisks.map((risk, i) => {
                      return (
                        <RiskReward
                          size="small"
                          name={risk.name}
                          key={i}
                          index={i}
                          severity={risk.severity}
                          description={risk.description}
                          hash={
                            risk.hash?.replace('#', '') as CompanySubSection
                          }
                        />
                      );
                    })}
                  </>
                ) : (
                  <>
                    <RiskRewardSkeleton />
                    <RiskRewardSkeleton />
                    <RiskRewardSkeleton />
                    <RiskRewardSkeleton />
                  </>
                )}
              </RiskRewardWrapper>
              <Button
                display="block"
                onClick={() => {
                  setOpenRiskModal(true);
                  trackEvent({
                    modifier: 'see-all-risk-checks',
                    action: 'click',
                  });
                }}
                styleType="loud"
                data-cy-id="summary-risk-modal-button"
              >
                See all risk checks
              </Button>
            </div>
          </RiskRewardGrid>
        </RiskRewardSection>
        <SnowflakeSection alignContent="space-between">
          <SnowflakeTitle>Snowflake Analysis</SnowflakeTitle>
          <SnowflakeChartWrapper
            id="overview-summary-snowflake"
            points={snowflakePoints || [0, 0, 0, 0, 0]}
            companyForFeedback={uniqueSymbol}
            feedbackLabel={COMPANY_SNOWFLAKE}
            preRenderHeight={'auto'}
            preRenderHeightSmDown={'auto'}
            chart={
              <SnowflakeChart data-cy-id="overview-summary-snowflake">
                <SnowflakeWrapper>
                  <Snowflake
                    points={snowflakePoints}
                    detailedPoints={analysisChecks}
                    tooltip={true}
                    routes={routes}
                    handleClick={handleSnowflakeClick}
                    disabled={isFund}
                    type={SnowflakeType.COMPANY}
                  />
                </SnowflakeWrapper>
                <SnowflakeCaption data-cy-id="company-fundamentals-desc">
                  {fundamentalsSentence && !isFund ? (
                    fundamentalsSentence
                  ) : (
                    <>&nbsp;</>
                  )}
                </SnowflakeCaption>
              </SnowflakeChart>
            }
            table={
              <SnowflakeChart>
                <SnowflakeTable
                  points={snowflakePoints || [0, 0, 0, 0, 0]}
                  tickerSymbol={tickerSymbol}
                />
              </SnowflakeChart>
            }
          />
        </SnowflakeSection>
      </Grid>
      {openRisksModal && (
        <RiskCheckModal
          companyShortName={companyName}
          companyTickerSymbol={tickerSymbol}
          riskSummaryList={allRisks}
          onClose={() => {
            setOpenRiskModal(false);
          }}
        />
      )}
      <ActionSheet
        open={openAboutCompany}
        onClose={() => setOpenAboutCompany(false)}
      >
        <Grid templateRows={`calc(40px + ${theme.safeAreaInsetTop}) auto`}>
          <Panel templateRows="max-content 1fr" row="2">
            <Header />
            <StyledWrapper>
              <ScrollContentWrapper>
                <AboutTheCompanyContent />
              </ScrollContentWrapper>
            </StyledWrapper>
          </Panel>
        </Grid>
      </ActionSheet>
    </SummarySection>
  );
}
